import { useEffect, useState } from "react";
import { FormRow } from "./FormRow";
import { LoadingSpinner } from "./LoadingSpinner";
import { useGoogleSheets } from "../hooks/useGoogleSheets";

export function GoogleSheetsForm({ user }) {
  const [rows, setRows] = useState([{ id: 1, brand: "", regionName: "", storeName: "", product: "", creditCost: "", wage: "", notes: "" }]);
  const { 
    brands, 
    stores, 
    products, 
    isLoading, 
    error, 
    submitData,
    isSubmitting,
    isDemoMode 
  } = useGoogleSheets();

  const addRow = () => {
    const newId = Math.max(...rows.map(r => r.id), 0) + 1;
    setRows([...rows, {
      id: newId,
      brand: "",
      regionName: "",
      storeName: "",
      product: "",
      creditCost: "",
      wage: "",
      notes: ""
    }]);
  };

  const removeRow = (id) => {
    if (rows.length > 1) {
      setRows(rows.filter(row => row.id !== id));
    }
  };

  const updateRow = (id, field, value) => {
    setRows(rows.map(row => {
      if (row.id === id) {
        const updatedRow = { ...row, [field]: value };
        
        // Reset dependent fields when brand changes
        if (field === 'brand') {
          updatedRow.regionName = '';
          updatedRow.storeName = '';
        }

        // Clear store name when region changes (user must select from filtered list)
        if (field === 'regionName') {
          updatedRow.storeName = '';
        }

        // Auto-populate region when store name changes (for react-select)
        if (field === 'storeName' && value && updatedRow.brand) {
          const store = stores[updatedRow.brand]?.find(s => s.storeName === value);
          if (store) {
            updatedRow.regionName = store.regionName;
          }
        }

        // Auto-populate credit cost when the product changes
        if (field === 'product' && value) {
          const product = products.find(p => p.name === value);
          if (product) {
            updatedRow.creditCost = product.creditCost.toString();
          }
        }
        
        return updatedRow;
      }
      return row;
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that all required fields are filled
    const isValid = rows.every(row =>
      row.brand && row.storeName && row.product
    );

    if (!isValid) {
      alert('Please fill in all required fields (Brand, Store Name, and Product)');
      return;
    }

    try {
      await submitData(rows, user?.email);
      // Reset form after successful submission
      setRows([{ id: 1, brand: "", regionName: "", storeName: "", product: "", creditCost: "", wage: "", notes: "" }]);
      alert('Data submitted successfully!');
    } catch (error) {
      alert('Error submitting data: ' + error.message);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Data</h3>
        <p className="text-red-600">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Demo Mode Banner */}
      {isDemoMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-blue-800">Demo Mode</h3>
              <p className="text-sm text-blue-600">
                Running with sample data. Credit costs are auto-populated and read-only. Configure Google Sheets API to use the real data.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Form Header */}
        <div className="border-b border-gray-200 pb-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Seasoned Credit Request</h2>
          <p className="text-sm text-gray-600">
          Choose a brand, region, store name and product to request Seasoned Sourcing. You can add multiple requests by choosing Add Row. Wages are not required but should be added if the ATS job wage needs overridden.
          </p>
        </div>

        {/* Form Rows */}
        <div className="space-y-4">
          {rows.map((row, index) => (
            <FormRow
              key={row.id}
              row={row}
              index={index}
              brands={brands}
              stores={stores}
              products={products}
              onUpdate={updateRow}
              onRemove={removeRow}
              canRemove={rows.length > 1}
            />
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={addRow}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Row
          </button>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-green-600 text-white px-8 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="small" />
                Submitting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Submit All
              </>
              )}
          </button>
        </div>
      </form>
    </div>
  );
}
